import { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/lib/stores/auth.store';
import { AuthLayout, DashboardLayout } from '@/components/templates';
//import { useNeedsOnboarding } from '@/features/onboarding';

import LoginPage from '@/pages/auth/login';
import SignupPage from '@/pages/auth/signup';
import ForgotPasswordPage from '@/pages/auth/forgot-password';
import ResendActivationPage from '@/pages/auth/resend-activation';
import OnboardingPage from '@/pages/onboarding';

import DashboardPage from '@/pages/dashboard';
import OrdersPage from '@/pages/orders';
import OrderPickupsPage from '@/pages/orders/pickups';
import OrderDetailPage from '@/pages/orders/[id]';
import OrderConversationPage from '@/pages/orders/[id]/conversation';
import MessagesPage from '@/pages/messages';
import ProductsPage from '@/pages/products';
import ProductDetailPage from '@/pages/products/[id]';
import NewProductPage from '@/pages/products/new';
import EditProductPage from '@/pages/products/[id]/edit';
import ShopsPage from '@/pages/shops';
import ShopDetailPage from '@/pages/shops/[id]';
import NewShopPage from '@/pages/shops/new';
import EditShopPage from '@/pages/shops/[id]/edit';
import PromotionsPage from '@/pages/promotions';
import NewPromotionPage from '@/pages/promotions/new';
import PromotionDetailPage from '@/pages/promotions/[id]';
import EditPromotionPage from '@/pages/promotions/[id]/edit';
import ShippingPage from '@/pages/shipping';
import NewShippingRatePage from '@/pages/shipping/rates/new';
import ShippingRateDetailPage from '@/pages/shipping/rates/[id]';
import EditShippingRatePage from '@/pages/shipping/rates/[id]/edit';
import WalletPage from '@/pages/wallet';
import WalletPayoutsPage from '@/pages/wallet/payouts';
import MetricsPage from '@/pages/metrics';
import EmployeesPage from '@/pages/employees';
import EmployeeDetailPage from '@/pages/employees/[id]';
import AdsPage from '@/pages/advertising';
import AdsCampaignsPage from '@/pages/advertising/campaigns';
import AdsCampaignNewPage from '@/pages/advertising/campaigns/new';
import AdsCampaignDetailPage from '@/pages/advertising/campaigns/[id]';
import AdsCampaignEditPage from '@/pages/advertising/campaigns/edit';
import AdsWalletPage from '@/pages/advertising/wallet';
import SettingsPage from '@/pages/settings';
import SettingsProfilePage from '@/pages/settings/profile';
import SettingsProfileEmailPage from '@/pages/settings/profile/email';
import SettingsProfilePasswordPage from '@/pages/settings/profile/password';
import SettingsCompanyPage from '@/pages/settings/company';
import SettingsNotificationsPage from '@/pages/settings/notifications';
import SettingsSubscriptionPage from '@/pages/settings/subscription';
import SettingsPreferencesPage from '@/pages/settings/preferences';
import SettingsAssociationsPage from '@/pages/settings/associations';
import ActivationSuccessPage from '@/pages/success/account/activation';
import SignupSuccessPage from '@/pages/success/account/signup';
import ActivationFailedPage from '@/pages/failed/account/activation';
import ResetPasswordPage from '@/pages/forget/password';
import DeleteSellerAccountPage from '@/pages/request/seller/delete';
import ConfirmDeleteSellerPage from '@/pages/request/seller/confirm';
import PrivacyPolicyProPage from '@/pages/legal/privacy-policy';
import CGVProPage from '@/pages/legal/cgv';
import TermsOfUseProPage from '@/pages/legal/terms-of-use';

// Maximum time to wait for auth store to hydrate (30 seconds for slow connections)
const AUTH_LOADING_TIMEOUT_MS = 30000;

function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [timedOut, setTimedOut] = useState(false);
//  const location = useLocation();
 // const { needsOnboarding, isLoading: isCheckingOnboarding } = useNeedsOnboarding();

  // Timeout protection: if loading takes too long, redirect to login
  useEffect(() => {
    if (!isLoading) {
      setTimedOut(false);
      return;
    }

    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('[Auth] Loading timeout reached, redirecting to login');
        setTimedOut(true);
      }
    }, AUTH_LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  if (isLoading && !timedOut) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated || timedOut) {
    return <Navigate to="/login" replace />;
  }

  // Check if user needs onboarding (skip if already on onboarding page)
  // if (!isCheckingOnboarding && needsOnboarding && location.pathname !== '/onboarding') {
  //   return <Navigate to="/onboarding" replace />;
  // }

  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

// Special route for onboarding - no dashboard layout
function OnboardingRoute() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setTimedOut(false);
      return;
    }

    const timeout = setTimeout(() => {
      if (isLoading) {
        setTimedOut(true);
      }
    }, AUTH_LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  if (isLoading && !timedOut) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated || timedOut) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setTimedOut(false);
      return;
    }

    const timeout = setTimeout(() => {
      if (isLoading) {
        setTimedOut(true);
      }
    }, AUTH_LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  // For public routes, if loading times out, just show the page
  if (isLoading && !timedOut) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Only redirect if authenticated (and not timed out)
  if (isAuthenticated && !timedOut) {
    return <Navigate to="/" replace />;
  }

  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  );
}

export const router = createBrowserRouter([
  // Standalone activation pages (no auth layout)
  { path: '/success/account/activation', element: <ActivationSuccessPage /> },
  { path: '/success/account/signup', element: <SignupSuccessPage /> },
  { path: '/failed/account/activation', element: <ActivationFailedPage /> },
  // Account deletion pages (public, no auth required) - Seller only
  { path: '/request/seller/delete', element: <DeleteSellerAccountPage /> },
  { path: '/request/seller/confirm', element: <ConfirmDeleteSellerPage /> },
  // Legal pages (public, no auth required)
  { path: '/privacy-policy/pro', element: <PrivacyPolicyProPage /> },
  { path: '/cgv/pro', element: <CGVProPage /> },
  { path: '/terms-of-use/pro', element: <TermsOfUseProPage /> },
  {
    element: <PublicRoute />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/forget/password', element: <ResetPasswordPage /> },
      { path: '/resend-activation', element: <ResendActivationPage /> },
    ],
  },
  {
    element: <OnboardingRoute />,
    children: [
      { path: '/onboarding', element: <OnboardingPage /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/', element: <DashboardPage /> },
      { path: '/orders', element: <OrdersPage /> },
      { path: '/orders/pickups', element: <OrderPickupsPage /> },
      { path: '/orders/:id', element: <OrderDetailPage /> },
      { path: '/orders/:id/conversation', element: <OrderConversationPage /> },
      { path: '/messages', element: <MessagesPage /> },
      { path: '/products', element: <ProductsPage /> },
      { path: '/products/new', element: <NewProductPage /> },
      { path: '/products/:id', element: <ProductDetailPage /> },
      { path: '/products/:id/edit', element: <EditProductPage /> },
      { path: '/shops', element: <ShopsPage /> },
      { path: '/shops/new', element: <NewShopPage /> },
      { path: '/shops/:id', element: <ShopDetailPage /> },
      { path: '/shops/:id/edit', element: <EditShopPage /> },
      { path: '/promotions', element: <PromotionsPage /> },
      { path: '/promotions/new', element: <NewPromotionPage /> },
      { path: '/promotions/:id', element: <PromotionDetailPage /> },
      { path: '/promotions/:id/edit', element: <EditPromotionPage /> },
      { path: '/shipping', element: <ShippingPage /> },
      { path: '/shipping/rates/new', element: <NewShippingRatePage /> },
      { path: '/shipping/rates/:id', element: <ShippingRateDetailPage /> },
      { path: '/shipping/rates/:id/edit', element: <EditShippingRatePage /> },
      { path: '/wallet', element: <WalletPage /> },
      { path: '/wallet/payouts', element: <WalletPayoutsPage /> },
      { path: '/metrics', element: <MetricsPage /> },
      { path: '/employees', element: <EmployeesPage /> },
      { path: '/employees/:id', element: <EmployeeDetailPage /> },
      { path: '/ads', element: <AdsPage /> },
      { path: '/ads/campaigns', element: <AdsCampaignsPage /> },
      { path: '/ads/campaigns/new', element: <AdsCampaignNewPage /> },
      { path: '/ads/campaigns/:id', element: <AdsCampaignDetailPage /> },
      { path: '/ads/campaigns/:id/edit', element: <AdsCampaignEditPage /> },
      { path: '/ads/wallet', element: <AdsWalletPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/settings/profile', element: <SettingsProfilePage /> },
      { path: '/settings/profile/email', element: <SettingsProfileEmailPage /> },
      { path: '/settings/profile/password', element: <SettingsProfilePasswordPage /> },
      { path: '/settings/company', element: <SettingsCompanyPage /> },
      { path: '/settings/notifications', element: <SettingsNotificationsPage /> },
      { path: '/settings/subscription', element: <SettingsSubscriptionPage /> },
      { path: '/settings/preferences', element: <SettingsPreferencesPage /> },
      { path: '/settings/associations', element: <SettingsAssociationsPage /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
